import toast from 'react-hot-toast'

import { FvButton, FvLinkButton } from '@fv/client-components'
import type { LoadDocument } from '@fv/client-types'
import { fvDateUtc } from '@fv/dates'
import {
  carrierDocTypes,
  carrierViewableDocTypes,
  type LoadDocumentType,
} from '@fv/models'

import { CarrierTooltip } from '@/components/CarrierTooltip'
import { EmptyList } from '@/components/shared/EmptyList'
import { documentTypes, supportMessage } from '@/constants'
import type { Opportunity } from '@/types/Opportunity'

import { useDeleteDocument } from './hooks/useDeleteDocument'
import { UploadDocumentField } from './UploadDocumentField'

type Props = {
  opportunity: Opportunity
}

export const Documents = ({ opportunity }: Props) => {
  const { documents = [], loadId } = opportunity

  const deleteDocument = useDeleteDocument()
  const allowedShipperDocs = [...carrierDocTypes, ...carrierViewableDocTypes]

  const shipperDocs = documents?.filter(
    d => d.source === 'shipper' && allowedShipperDocs.includes(d.type),
  )
  const allowedSystemDocs: LoadDocumentType[] = ['bol', 'rate-confirmation']
  const systemDocs = documents?.filter(
    d => d.source === 'system' && allowedSystemDocs.includes(d.type),
  )
  const carrierDocs = documents?.filter(d => d.source === 'carrier')

  const documentGroups = [
    {
      docs: carrierDocs,
      itemClass: 'flex flex-1 items-center py-2 leading-none',
      key: 'carrier',
      label: 'User Generated',
      showDelete: true,
      useLinkButton: false,
    },
    {
      docs: shipperDocs,
      itemClass: 'py-2 leading-none',
      key: 'shipper',
      label: 'Shipper Provided',
      showDelete: false,
      useLinkButton: false,
    },
    {
      docs: systemDocs,
      itemClass: 'py-2',
      key: 'system',
      label: 'Freightview Generated',
      showDelete: false,
      useLinkButton: true,
    },
  ] as const

  const visibleDocumentGroups = documentGroups.filter(
    group => group.docs.length,
  )

  const deleteSelectedDocument = (document: LoadDocument) => {
    if (deleteDocument.isPending) return

    if (
      window.confirm('Are you sure you wish to delete the selected document?')
    ) {
      deleteDocument.mutateAsync({ document, loadId }).catch(() => {
        toast.error(`Unable to remove document, ${supportMessage}`)
      })
    }
  }

  return (
    <>
      <div className="overflow-auto -mx-6 px-6">
        {visibleDocumentGroups.length ? (
          visibleDocumentGroups.map(group => (
            <div className="[&+&]:mt-4" key={group.key}>
              <div className="border-b border-fv-gray mb-2 pb-1">
                {group.label}
              </div>

              <ul className="divide-fv-gray flex-1 divide-y divide-dotted">
                {group.docs.map(file => {
                  const docType = documentTypes.find(t => t.value === file.type)
                  const label = docType?.text || file.fileName

                  return (
                    <li className={group.itemClass} key={file._id}>
                      <CarrierTooltip label={getUploadDateTooltip(file)}>
                        {group.useLinkButton ? (
                          <FvLinkButton
                            icon="file"
                            className="block"
                            onClick={() => {
                              window.open(file.url)
                            }}
                          >
                            {label}
                          </FvLinkButton>
                        ) : (
                          <FvButton
                            icon="file"
                            onClick={() => {
                              window.open(file.url)
                            }}
                          >
                            {label}
                          </FvButton>
                        )}
                      </CarrierTooltip>

                      {group.showDelete && (
                        <FvButton
                          className="ml-auto"
                          icon={deleteDocument.isPending ? 'spinner' : 'trash'}
                          iconClass="text-fv-orange-bright"
                          onClick={() => {
                            deleteSelectedDocument(file)
                          }}
                        />
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          ))
        ) : (
          <EmptyList message="No documents available." />
        )}
      </div>
      {(opportunity.status === 'awarded' ||
        opportunity.status === 'confirmed') && (
        <div className="mt-auto bg-fv-blue-100 border-t-2 border-fv-blue-300 p-6 -mx-6 -mb-8">
          <label className="block mb-2">Upload a document</label>
          <UploadDocumentField
            className="flex flex-wrap"
            opportunity={opportunity}
          />
        </div>
      )}
    </>
  )
}

function getUploadDateTooltip(file: LoadDocument) {
  if (file.uploadDate) {
    return `Uploaded ${fvDateUtc(file.uploadDate).local().format('YYYY-MM-DD')}`
  }
  return 'Upload date unavailable'
}
